import { useMemo, useRef, useState } from 'react';
import { api, mediaUrl, uploadMedia } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { Recorder } from '../components/Recorder';
import { ErrorBox, Loading, Notice, PageHeader } from '../components/ui';

interface Person {
  id: string;
  display_name: string;
  relation_label: string | null;
  is_living: boolean;
  hometown: string | null;
  side: 'paternal' | 'maternal' | 'self' | null;
  generation: number;
  child_profile_id: string | null;
  notes: string | null;
}
interface Relation {
  id: string;
  from_person_id: string;
  to_person_id: string;
  type: 'parent_of' | 'spouse_of';
}
interface Clip {
  id: string;
  person_id: string;
  label: string;
  media_asset_id: string;
}
interface TreeResp {
  people: Person[];
  relations: Relation[];
  clips: Clip[];
}

const W = 960;
const ROW = 150;
const R = 38;

/**
 * Lay out generations top (elders) to bottom (children).
 * Each person sits under the midpoint of their parents, spouses sit side by side,
 * and people with no parents on the tree fall back to paternal-left / maternal-right.
 */
function layout(people: Person[], relations: Relation[]) {
  const gens = [...new Set(people.map((p) => p.generation))].sort((a, b) => b - a);
  const pos = new Map<string, { x: number; y: number }>();
  const sideKey = { paternal: 0.2, self: 0.5, maternal: 0.8 } as const;
  const parentsOf = new Map<string, string[]>();
  const spouseOf = new Map<string, string>();
  for (const r of relations) {
    if (r.type === 'parent_of') parentsOf.set(r.to_person_id, [...(parentsOf.get(r.to_person_id) ?? []), r.from_person_id]);
    else { spouseOf.set(r.from_person_id, r.to_person_id); spouseOf.set(r.to_person_id, r.from_person_id); }
  }
  gens.forEach((g, gi) => {
    const row = people.filter((p) => p.generation === g);
    const key = new Map<string, number>();
    for (const p of row) {
      const placed = (parentsOf.get(p.id) ?? []).map((id) => pos.get(id)).filter(Boolean) as { x: number }[];
      key.set(p.id, placed.length ? placed.reduce((s, q) => s + q.x, 0) / placed.length / W : sideKey[p.side ?? 'self']);
    }
    // A spouse without parents on the tree sits beside their partner.
    for (const p of row) {
      const sp = spouseOf.get(p.id);
      if (sp && key.has(sp) && !(parentsOf.get(p.id) ?? []).length && (parentsOf.get(sp) ?? []).length) key.set(p.id, key.get(sp)! + 0.001);
    }
    // Group spouses into one unit so they stay adjacent.
    const seen = new Set<string>();
    const units: { k: number; ids: string[] }[] = [];
    for (const p of row) {
      if (seen.has(p.id)) continue;
      const sp = spouseOf.get(p.id);
      const ids = sp && key.has(sp) && !seen.has(sp) ? [p.id, sp].sort((a, b) => key.get(a)! - key.get(b)!) : [p.id];
      ids.forEach((id) => seen.add(id));
      units.push({ k: ids.reduce((s, id) => s + key.get(id)!, 0) / ids.length, ids });
    }
    const ordered = units.sort((a, b) => a.k - b.k).flatMap((u) => u.ids);
    ordered.forEach((id, i) => pos.set(id, { x: ((i + 1) * W) / (ordered.length + 1), y: 70 + gi * ROW }));
  });
  // Draw each set of siblings from their parents' midpoint, as a tidy bracket.
  const groups = new Map<string, { parents: string[]; kids: string[] }>();
  for (const [kid, ps] of parentsOf) {
    const k = [...ps].sort().join('|');
    groups.set(k, { parents: ps, kids: [...(groups.get(k)?.kids ?? []), kid] });
  }
  const links: string[] = [];
  for (const { parents, kids } of groups.values()) {
    const ps = parents.map((id) => pos.get(id)).filter(Boolean) as { x: number; y: number }[];
    const ks = kids.map((id) => pos.get(id)).filter(Boolean) as { x: number; y: number }[];
    if (!ps.length || !ks.length) continue;
    const mx = ps.reduce((s, q) => s + q.x, 0) / ps.length;
    const py = ps[0]!.y;
    const ky = Math.min(...ks.map((q) => q.y));
    const bar = (py + ky) / 2 + 12;
    const xs = [mx, ...ks.map((q) => q.x)];
    links.push(`M${mx},${ps.length > 1 ? py : py + R} V${bar} M${Math.min(...xs)},${bar} H${Math.max(...xs)} ` + ks.map((q) => `M${q.x},${bar} V${q.y - R}`).join(' '));
  }
  return { pos, links, height: 70 + gens.length * ROW };
}

export function VoiceTreePage() {
  const { user } = useAuth();
  const { data, error, loading, reload } = useApi<TreeResp>('/tree');
  const [selected, setSelected] = useState<Person | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const act = useAction();
  const canEdit = user?.role === 'parent' || user?.role === 'grandparent';
  const [newPerson, setNewPerson] = useState({ displayName: '', relationLabel: '', generation: '2', side: 'paternal', hometown: '', isLiving: true, parentOf: '' });

  const lay = useMemo(() => (data ? layout(data.people, data.relations) : null), [data]);
  if (loading) return <Loading />;
  if (error || !data || !lay) return <ErrorBox error={error} />;

  const clipsFor = (id: string) => data.clips.filter((c) => c.person_id === id);

  /** Shared by tapping a person and by tapping one of their clips, so the tree
   *  always shows which voice is sounding — and says so when one will not play. */
  const playClip = async (personId: string, mediaAssetId: string) => {
    const url = await act.run(() => mediaUrl(mediaAssetId));
    if (!url) return;
    audio.current?.pause();
    audio.current = new Audio(url);
    setPlaying(personId);
    audio.current.onended = () => setPlaying(null);
    audio.current.play().catch(() => {
      setPlaying(null);
      act.setError('That recording would not play here. It is still safely stored — try the Story Vault.');
    });
  };

  const play = async (p: Person) => {
    setSelected(p);
    const clip = clipsFor(p.id)[0];
    if (!clip) return;
    await playClip(p.id, clip.media_asset_id);
  };

  return (
    <>
      <PageHeader title="Voice Tree" termKey="page.voice_tree" lead="Tap a grandparent to hear their voice. Circles with a warm border have recordings; dashed circles are loved ones who have passed." />
      <ErrorBox error={act.error} />
      <Notice text={act.notice} />
      <div className="tree-frame">
        <svg className="tree-svg" viewBox={`0 0 ${W} ${lay.height}`} role="group" aria-label="Family tree">
          <defs>
            {/* adire-style dot field, the cloth this family's names are printed on */}
            <pattern id="adire" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="12" cy="12" r="1.3" className="tree-dot" />
            </pattern>
            <linearGradient id="nodeFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="node-stop-1" />
              <stop offset="100%" className="node-stop-2" />
            </linearGradient>
            <linearGradient id="voiceRing" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f0b93c" />
              <stop offset="100%" stopColor="#d9791e" />
            </linearGradient>
            <linearGradient id="linkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="link-stop-1" />
              <stop offset="100%" className="link-stop-2" />
            </linearGradient>
            <filter id="voiceGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="7" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect x="0" y="0" width={W} height={lay.height} fill="url(#adire)" />
          {data.relations.filter((r) => r.type === 'spouse_of').map((r) => {
            const a = lay.pos.get(r.from_person_id);
            const b = lay.pos.get(r.to_person_id);
            if (!a || !b) return null;
            return (
              <g key={r.id}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="url(#voiceRing)" strokeWidth={2.5} strokeDasharray="1 7" strokeLinecap="round" opacity={0.85} />
                <circle cx={(a.x + b.x) / 2} cy={a.y} r={4.5} fill="url(#voiceRing)" />
              </g>
            );
          })}
          {lay.links.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="url(#linkGrad)" strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {data.people.map((p) => {
            const at = lay.pos.get(p.id)!;
            const hasVoice = clipsFor(p.id).length > 0;
            return (
              <g
                key={p.id}
                className={`tree-node ${hasVoice ? 'has-voice' : ''} ${p.is_living ? '' : 'deceased'} ${playing === p.id ? 'playing' : ''} ${selected?.id === p.id ? 'selected' : ''}`}
                transform={`translate(${at.x},${at.y})`}
                tabIndex={0}
                role="button"
                aria-label={`${p.relation_label ?? ''} ${p.display_name}${hasVoice ? ', play voice' : ''}${p.is_living ? '' : ', passed away'}`}
                onClick={() => play(p)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), play(p))}
              >
                {(hasVoice || selected?.id === p.id) && <circle className="node-halo" r={R + 7} />}
                <circle className="node-circle" r={R} />
                <text textAnchor="middle" dy="7" fontSize="24" aria-hidden="true">
                  {p.child_profile_id ? '🌱' : hasVoice ? (playing === p.id ? '🔊' : '🎙️') : p.generation >= 2 ? '👵🏾' : '🧑🏾'}
                </text>
                {hasVoice && (
                  <g transform={`translate(${R - 6}, ${-R - 2})`} aria-hidden="true">
                    <circle r="11" className="node-chip" />
                    <text textAnchor="middle" dy="4" fontSize="11.5" fontWeight="700" className="node-chip-text">
                      {clipsFor(p.id).length}
                    </text>
                  </g>
                )}
                <text textAnchor="middle" y={R + 22} fontFamily="var(--font-yo)" fontSize="17" fontWeight="700" className="tree-name">
                  {p.display_name}
                </text>
                {p.relation_label && (
                  <text textAnchor="middle" y={R + 40} fontFamily="var(--font-yo)" fontSize="13" className="tree-rel">
                    {p.relation_label}
                    {p.hometown ? ` · ${p.hometown}` : ''}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        <div className="tree-legend" aria-hidden="true">
          <span>
            <i className="key voice" /> has recordings
          </span>
          <span>
            <i className="key gone" /> passed away
          </span>
          <span>
            <i className="key child" /> child (never shown publicly)
          </span>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 16 }}>
        {selected && (
          <div className="card stack person-card" aria-live="polite">
            <div className="person-head">
              <span className="person-face" aria-hidden="true">
                {selected.child_profile_id ? '🌱' : selected.generation >= 2 ? '👵🏾' : '🧑🏾'}
              </span>
              <div>
                <h2 className="yo" style={{ margin: 0 }}>
                  {selected.relation_label} {selected.display_name}
                </h2>
                <p className="muted small" style={{ margin: 0 }}>
                  {[selected.hometown, selected.is_living ? null : 'voice preserved'].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            {selected.notes && <p className="muted">{selected.notes}</p>}
            {clipsFor(selected.id).length === 0 ? (
              <p className="muted small">No recordings yet. Ask them for one story — that is how every vault starts.</p>
            ) : (
              <ul className="clip-list">
                {clipsFor(selected.id).map((c) => (
                  <li key={c.id}>
                    <button
                      className="clip"
                      onClick={() => void playClip(selected.id, c.media_asset_id)}
                    >
                      <span className="clip-play" aria-hidden="true">
                        ▶
                      </span>
                      <span className="yo clip-label">{c.label}</span>
                      <span className="clip-wave" aria-hidden="true">
                        {[7, 13, 20, 11, 17, 24, 14, 9, 18, 12].map((h, i) => (
                          <i key={i} style={{ height: h }} />
                        ))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {canEdit && !selected.child_profile_id && (
              <div className="stack">
                <h3>Add a voice recording</h3>
                <Recorder
                  label={`Record ${selected.display_name}`}
                  onRecorded={async (blob, seconds) => {
                    const label = prompt('What is this recording? e.g. “Ẹ káàárọ̀ — good morning”') ?? 'Voice note';
                    await act.run(async () => {
                      const id = await uploadMedia(blob, 'elder_voice');
                      await api(`/tree/people/${selected.id}/voice-clips`, { body: { mediaAssetId: id, label, durationSeconds: seconds } });
                    }, 'Saved to the Voice Tree.');
                    reload();
                  }}
                />
              </div>
            )}
          </div>
        )}
        {canEdit && (
          <form
            className="card stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const { parentOf, ...person } = newPerson;
              const created = await act.run(
                () => api<{ person: Person }>('/tree/people', { body: { ...person, generation: Number(person.generation), hometown: person.hometown || undefined, relationLabel: person.relationLabel || undefined } }),
                'Added to the tree.',
              );
              // Draw the branch straight away: without a parent_of relation the person floats
              // on their own row and the tree never joins up.
              if (created?.person?.id && parentOf) {
                await act.run(() => api('/tree/relations', { body: { fromPersonId: created.person.id, toPersonId: parentOf, type: 'parent_of' } }));
              }
              setNewPerson({ ...newPerson, displayName: '', relationLabel: '', hometown: '' });
              reload();
            }}
          >
            <h2>Add someone</h2>
            <div className="row">
              <input aria-label="Name" placeholder="Name" className="yo" value={newPerson.displayName} onChange={(e) => setNewPerson({ ...newPerson, displayName: e.target.value })} required />
              <input aria-label="Relation" placeholder="e.g. Ìyá àgbà" className="yo" value={newPerson.relationLabel} onChange={(e) => setNewPerson({ ...newPerson, relationLabel: e.target.value })} />
              <input aria-label="Hometown" placeholder="Hometown" className="yo" value={newPerson.hometown} onChange={(e) => setNewPerson({ ...newPerson, hometown: e.target.value })} />
            </div>
            <div className="row">
              <select aria-label="Generation" value={newPerson.generation} onChange={(e) => setNewPerson({ ...newPerson, generation: e.target.value })}>
                <option value="3">Great-grandparents</option>
                <option value="2">Grandparents</option>
                <option value="1">Parents, aunts, uncles</option>
              </select>
              <select aria-label="Side" value={newPerson.side} onChange={(e) => setNewPerson({ ...newPerson, side: e.target.value })}>
                <option value="paternal">Father’s side</option>
                <option value="maternal">Mother’s side</option>
                <option value="self">Our household</option>
              </select>
              <select aria-label="Parent of" value={newPerson.parentOf} onChange={(e) => setNewPerson({ ...newPerson, parentOf: e.target.value })}>
                <option value="">Not linked yet</option>
                {data.people.map((p) => (
                  <option key={p.id} value={p.id}>
                    Parent of {p.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="row">
              <label className="check">
                <input type="checkbox" checked={!newPerson.isLiving} onChange={(e) => setNewPerson({ ...newPerson, isLiving: !e.target.checked })} /> Has passed away
              </label>
              <button className="btn small">Add</button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
